// The Briefing when a task, a NOTE or an EVENT is open in the box (frame 40007893:378129).
//
// busy-my-web#67 built this grid for tasks alone, because the frame draws a task. Pouria
// (2026-08-16): "we can see the task in middle of story and details briefing about task — same for
// note and events." So the shell is one shell — header "Briefing · <title>", two columns of
// labelled facts, the editor, the Work-board column — and only the LEFT column's rows differ, since
// that is the only part of it that is about the entity's own kind:
//
//   task   Due · Reminder · Important · Waiting on
//   event  When (start–end) · Reminder · Important
//   note   Important
//
// The right column is the rest of what the user has put ON the thing: Checklist · Attachments.
//
// ── ONLY WHAT THE USER CAN SET (busy-my-web#373) ────────────────────────────────────────────────
// Fatemeh, in the Slack thread that answered both open questions: "anything that is not one of the
// ACTIONS of a task or an email or … counts as extra information. What can a task have? important /
// waiting on / due date / reminder / checklist / attachment. These are the things the user can
// define for a task, so in the Briefing we have only these. But in the timeline we can have both of
// them" — "both of them" being Created and Created from.
//
// So this grid lost four rows it used to draw: `Created` and `Created from` (the entity's
// provenance) and `In` / `Related` / `Work-board` (where it happens to sit right now, and what is
// in hand beside it). None of the five is something the user typed onto the entity; all five are
// the app reporting about it. Asked directly whether In / Related / Work-board should go too,
// Fatemeh answered "اره" — yes.
//
// WHERE THEY WENT: the entity's own rail (lib/entityTimeline.js, drawn by
// SidePanel/EntityTimelinePanel.vue) already heads its one item with the creation — "Task created",
// stamped with the created instant — and lists "Created from" as its first detail, which is exactly
// what frame 40008716-284181 draws down the right-hand side. Nothing had to move there; the rule
// this file now follows is what made the briefing stop repeating it.
//
// The rule from #67 still governs everything here: a fact is drawn only when the payload carries
// it. That is what makes one grid serve three kinds — a note has no Due date, so it has no Due row,
// for exactly the same reason a task whose reminder was never set has no Reminder row. Nothing is
// invented to fill a column, and no kind gets an empty cell to keep the two columns the same
// height.
//
// Pure, over values the store already holds (briefing/openEntity's record, the story timeline
// event, the entity's documents, and the box's own live toggles).

import { shortClock } from '@/lib/timelineTime'
import { parseUtc, dueStatus, dueAmount, statusText, statusTokenColor, statusMeta } from '@/lib/dueStatus'
import { eventStatus, eventLabel } from '@/lib/eventTime'
import { factStamp, attachmentsText } from './taskFacts'
import { countLabel } from './rows'
// One reading of "is this task done" for the box, the header and the store request (#415).
import { isTaskCompleted } from '@/lib/taskCompletion'
// The creation strip's own chips (icon + the words a person would use), reused for an OPEN entity
// since the 2026-09-15 parity audit (busy-my-web#466 / #474) — see `entityChips` below.
import { creationBriefing } from './creationFacts'

// busy-my-web#609 (Saffet 2026-10-09): the shared `countLabel` — thousands separator, capital after the count.
const plural = (n, word) => countLabel(n, word)

/** The three kinds this grid is drawn for — the box's own menu names. */
export const BRIEFED_MENUS = ['tasks', 'notes', 'events']

/** Is this something the briefing grid describes? Used by the store and by the box's watchers. */
export const isBriefedMenu = (menu) => BRIEFED_MENUS.includes(String(menu || ''))

/**
 * The heading over the open entity's own files on the Work-board ("Task attachments"). Named after
 * the KIND rather than a flat "Attachments", because the group sits directly under "Selected items"
 * and has to say whose files these are — they are not part of the selection.
 */
export function attachmentsHeading(menu) {
  // Contacts carry attachments too since busy-my-web#240 — the Work-board names theirs the same way.
  return { tasks: 'Task attachments', notes: 'Note attachments', events: 'Event attachments', contacts: 'Contact attachments' }[menu] || 'Attachments'
}

const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

/**
 * An event's "When": "Aug 21, 6pm – 8pm" inside one day, "Aug 21, 6pm – Aug 22, 9am" across two.
 *
 * A same-day end repeats its own date if written in full, and the frame's facts are single-line
 * values — so the second date is written only when it is genuinely a different day. An event with
 * no end (or an unreadable one) is stated as its start alone rather than as a range with a hole in
 * it.
 */
export function whenRange(start, end) {
  const from = factStamp(start)
  if (!from) return ''
  const a = start instanceof Date ? start : parseUtc(start)
  const b = end instanceof Date ? end : (end ? parseUtc(end) : null)
  if (!b || isNaN(b.getTime()) || !a || isNaN(a.getTime()) || b.getTime() <= a.getTime()) return from
  return sameDay(a, b) ? `${from} – ${shortClock(b) || factStamp(b)}` : `${from} – ${factStamp(b)}`
}

const first = (...values) => values.find(v => v !== null && v !== undefined && v !== '') ?? null

/**
 * The one shape the grid reads, whatever kind of record it was handed.
 *
 * Each store spells its own record differently — a task has `dueDate`/`createDate`, an event in the
 * calendar pool has `startDateTime`/`endDateTime` as Date objects while the API's own shape is
 * `startDatetime`/`endDatetime`, a note has neither. Normalising here keeps that spread in ONE
 * place: the row builders below then read plain names, and a new field spelling is a line in this
 * function rather than a condition in three rows.
 */
export function entityWhen(menu, entity, event) {
  const e = entity || {}
  return {
    title: String(e.title || e.summary || e.subject || '').trim(),
    // The two flags the action row used to report in a strip UNDER the box (Pouria, 2026-08-18:
    // "important and wating on at btn should be remove and show it at top inside briefing"). They
    // are facts about the entity like any other, so they are read here with the dates. "Waiting on"
    // is spelled two ways depending on which store the record came from — a boolean on the edit
    // payload, the task's own `status` in the list.
    important: !!(e.isImportant ?? e.important),
    waiting: !!(e.waitingOn ?? (String(e.status || '').toLowerCase() === 'waitingon')),
    due: first(e.dueDate, menu === 'tasks' ? event?.dueDate : null),
    remind: first(e.remindDate, e.reminderDate),
    created: first(e.createDate, e.createdDate, event?.createdDate),
    // An event opened from a story step may only be the step: the timeline event's own date is the
    // start it was created with, which is a truer answer than no "When" row at all.
    start: first(e.startDatetime, e.startDateTime, e.start, menu === 'events' ? event?.dueDate : null),
    end: first(e.endDatetime, e.endDateTime, e.end),
  }
}

const fact = (key, label, value) => (String(value ?? '').trim() ? { key, label, value: String(value).trim() } : null)

/**
 * The header's own state line — "Overdue 2 hours" / "Due soon by 1 hour" — in the token sheet's
 * colour for that state (Figma 40008776:355610/355611, the task-edit section 40003506:80500 draws
 * it beside the "Briefing · <title>" line, in `color state/due soon` #FF8800).
 *
 * Only what is NEWS: overdue and due-soon. `dueStatus` also names 'upcoming', but "Coming up 12
 * hours" beside a Due fact that already reads "Tomorrow, 9am" is the same sentence twice — the
 * header line exists to say the schedule has gone wrong, not to repeat it. Same wording and same
 * colour token as the Global timeline's status line, so an overdue task reads identically wherever
 * it is seen.
 *
 * @returns `{ text, color }`, or null when there is no date or nothing to warn about.
 */
export function entityStatusLine(due, now = Date.now(), { kind = 'tasks', end = null, completed = false } = {}) {
  // A COMPLETED task is not late and is not due: the frame drops the due label entirely and says
  // "Completed" in the sheet's green (busy-my-web#415, 40008879-383276). The extra `completed` flag
  // is what tells the header to draw a tick rather than the bell.
  if (completed && kind !== 'events') {
    return { text: statusMeta('completed').label, color: statusTokenColor('completed'), completed: true }
  }
  // An EVENT is never late (busy-my-web#238): the news about one is that it is about to happen or is
  // happening, and once it is over there is no warning left to give — the "When" fact below already
  // says which day it was. Its wording is the shared lifecycle ladder (lib/eventTime.js), so the
  // header line, the global feed and the story rail cannot describe one event three ways.
  if (kind === 'events') {
    const stage = eventStatus(due, { end, now })
    // "Coming up" for an event that is merely ahead (2026-09-15 parity audit C-9, frames
    // 40008863:378008 / 40008894:384566 draw it beside "Briefing . Standup meeting") — the bare
    // words, not the amount: the When fact right under it already says the day.
    if (stage === 'upcoming') return { text: 'Coming up', color: statusTokenColor('upcoming') }
    if (stage !== 'dueSoon' && stage !== 'now') return null
    return { text: eventLabel(due, { end, now }), color: statusTokenColor(stage) }
  }
  const status = dueStatus(due, now)
  // …and the same bare "Coming up" for a task whose date is simply ahead (2026-09-15 parity audit
  // B-5, frame 40008783:363509: the header reads "Coming up" once the due date moved to Aug 31).
  // This SUPERSEDES the "only what is NEWS" rule above for the upcoming rung — the frames draw the
  // state whatever it is; overdue / due-soon keep their amount because that is what those say.
  if (status === 'upcoming') return { text: 'Coming up', color: statusTokenColor('upcoming') }
  if (status !== 'overdue' && status !== 'dueSoon') return null
  const text = statusText(status, dueAmount(due, now))
  return text ? { text, color: statusTokenColor(status) } : null
}

/**
 * The header + subject line for an OPEN CONTACT (2026-09-15 parity audit C-9/C-10, frames
 * 40008931:392836 / 40008935:397620): "Briefing . Saffet Mustafa" over the same fact chips the
 * creation strip draws (Email / Phone number / Job title | Company) — the chips come from
 * `entityChips`; this is the shell. A contact has no due state, so `status` is null, and no
 * labelled facts, so the two columns are empty (the chips are the facts).
 */
export function contactFacts({ entity = null, title = '' } = {}) {
  const name = String(entity?.name || entity?.title || title || '').trim()
  if (!name) return null
  return { title: `Briefing · ${name}`, icon: 'receipt-text', status: null, left: [], right: [] }
}

/**
 * The fact CHIPS an open entity's Briefing draws (2026-09-15 parity audit B-5 / C-9 / C-10, frames
 * 40008716:284181, 40008810:369754, 40008863:378008, 40008931:392836): the SAME icon-and-words rows
 * the creation strip draws — "Today, 12am", "30 minutes before", "★ Important", "4 Checklists",
 * "1 Attachment" — in the same two columns. The labelled "Due / Reminder / Important: Yes" grid
 * `entityFacts` builds is what the frames replaced; it is kept as the record of WHAT is set (the
 * header's status line and the specs read it) while these are what is DRAWN.
 *
 * Same arguments as `creationBriefing`; answers `{ left, right }`, both possibly empty.
 */
export function entityChips(menu, state = {}, format = {}) {
  const b = creationBriefing(menu, state, format)
  if (!b || b.empty) return { left: [], right: [] }
  return { left: b.left || [], right: b.right || [] }
}

/**
 * The facts grid for whatever is open in the box.
 *
 * @param menu      'tasks' | 'notes' | 'events' — the box's own name for the kind
 * @param entity    the record (v1/task, v1/note, the calendar pool's event), when it has resolved
 * @param event     the story timeline event behind it — read for the dates it can supply. Where it
 *                  came FROM is the timeline rail's answer now, not this grid's (busy-my-web#373)
 * @param docs      the entity's documents (document/timelines/list)
 * @param important overrides the record's own flag — the box's LIVE toggle while it is being edited
 * @param waiting   the same for "Waiting on" (tasks only)
 * @param checklist how many checklist rows the task has — the box's LIVE list while it is open
 * @param due       overrides the record's own due date — the box's LIVE date pane while it is open
 * @param remind    the same for the reminder
 * @param now       the clock, for the header's state line (injectable so it can be tested)
 * @returns `{ title, icon, status, left, right }`, or null when there is nothing to describe
 */
export function entityFacts({ menu = 'tasks', entity = null, event = null, docs = [], important = undefined, waiting = undefined, completed = undefined, checklist = 0, due = undefined, remind = undefined, now = undefined } = {}) {
  if (!isBriefedMenu(menu)) return null
  if (!entity && !event) return null
  const w = entityWhen(menu, entity, event)
  const title = w.title || String(event?.title || '').trim()

  // The left column IS the kind. A note has no schedule of its own, so its left column is the
  // flags alone rather than two blank rows kept to level the columns.
  //
  // Due and Reminder read the box's LIVE panes when it hands them over, exactly as Important,
  // Waiting on and the checklist already did (busy-my-web#168). They used to be the only schedule
  // facts still read off the RECORD, so on an open task the count of a checklist just typed moved
  // at once while a due date set beside it did not appear at all until the save came back — the
  // report the user actually saw was "a checklist, and no dates". One rule for every live fact.
  const liveDue = due === undefined ? w.due : due
  const liveRemind = remind === undefined ? w.remind : remind
  const dated = menu === 'events'
    ? [fact('when', 'When', whenRange(w.start, w.end))]
    : menu === 'tasks'
      ? [fact('due', 'Due', factStamp(liveDue))]
      : []
  // The state flags, by the same rule as every other row: drawn only when they are ON. "Important"
  // belongs to all three kinds; "Waiting on" is a task's own status and exists nowhere else — which
  // is exactly where the action row offers its toggle.
  const isImportant = important === undefined ? w.important : !!important
  const isWaiting = menu === 'tasks' && (waiting === undefined ? w.waiting : !!waiting)
  const left = [
    ...dated,
    // A note cannot be reminded about — the field exists on tasks and events only.
    menu === 'notes' ? null : fact('reminder', 'Reminder', factStamp(liveRemind)),
    isImportant ? fact('important', 'Important', 'Yes') : null,
    isWaiting ? fact('waiting', 'Waiting on', 'Yes') : null,
  ].filter(Boolean)

  const right = [
    // How much of a task there is TO do, beside what it has in hand — the task-edit section
    // (40008778:362820, "4 Checklist items") reports the count in the briefing's second column,
    // directly above attachments, even though the rows themselves are drawn in the box below. The
    // grid described what a task IS everywhere except here. Tasks only: nothing else has one.
    menu === 'tasks' ? fact('checklist', 'Checklist', checklist > 0 ? plural(checklist, 'item') : '') : null,
    fact('attachments', 'Attachments', attachmentsText(docs, entity?.attachmentCount, { capital: true })),
  ].filter(Boolean)

  // A grid with NO facts is still a grid (busy-my-web#373, guarding busy-my-web#335). Before the
  // provenance rows left, every saved entity had at least `Created`, so "no facts" only ever meant
  // "nothing is open"; now a note the user has not starred, dated or attached to has none of the
  // six properties, and that is an ordinary state. This block is not only the facts — it is also
  // the breadcrumb, the subject line and the ✕ that closes the entity (#335, Saffet: he looked for
  // the ✕ on a task and there wasn't one). So it is withheld only when there is nothing to HEAD it
  // with either, which is the same "nothing is open" it always meant.
  if (!left.length && !right.length && !title) return null
  // The frame's own header glyph is the receipt (a briefing is a summary sheet, not a tick) and it
  // is the same for all three kinds — the title says which entity this is about.
  // The state line rides beside it: a task's due date, an event's start (a note has neither).
  // …and completion is live too: the tick in the action row moves the header before anything has
  // been saved back, exactly as Important and the checklist already do (busy-my-web#415).
  // Read off the RECORD when the box has not handed a live answer over — `entityWhen` deliberately
  // keeps its own shape (it is the dates and the two flags, and specs pin it exactly).
  const isCompleted = menu === 'tasks' && (completed === undefined ? isTaskCompleted(entity) : !!completed)
  const status = entityStatusLine(menu === 'events' ? w.start : menu === 'tasks' ? liveDue : null, now, { kind: menu, end: w.end, completed: isCompleted })
  return { title: title ? `Briefing · ${title}` : 'Briefing', icon: 'receipt-text', status, left, right }
}

export default entityFacts
